/**
 * OfficeManagementPage (Phase-2, Module 1 — office admin side)
 *
 * Lists offices and the staff assigned to each office. Office admin can:
 *  - View staff of an office (dialog)
 *  - Assign a user to an office (POST /office-staff)
 *  - Edit a staff assignment's designation/department/permissions (PUT /office-staff/{id})
 *  - Remove a staff assignment (DELETE /office-staff/{id})
 * Read + manage only — non-breaking, reuses existing OfficeStaff endpoints.
 *
 * @author Civilhealth Development Team
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Button, Chip, Stack, Typography, Divider, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControlLabel, Checkbox, Autocomplete,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Business as OfficeIcon, Delete as DeleteIcon, Edit as EditIcon, PersonAdd as AssignIcon } from '@mui/icons-material';
import { PageScaffold, StatusBadge, ConfirmDialog } from '../../components/common';
import {
  OfficeApi, OfficeStaffApi,
  type OfficeRow, type OfficeStaffRow, type OfficeStaffRequest, type OfficeStaffUpdateRequest, type AssignableUser,
} from '../../services/phase2Api';

const today = () => new Date().toISOString().slice(0, 10);

const emptyAssign = {
  designation: '', department: '', joiningDate: today(),
  isPrimaryOffice: false, canCreateRequests: true, canUploadDocuments: true, canTrackRequests: true, notes: '',
};

const OfficeManagementPage: React.FC = () => {
  const [offices, setOffices] = useState<OfficeRow[]>([]);
  const [selected, setSelected] = useState<OfficeRow | null>(null);
  const [staff, setStaff] = useState<OfficeStaffRow[]>([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removeId, setRemoveId] = useState<number | null>(null);

  // Assign dialog
  const [assignOffice, setAssignOffice] = useState<OfficeRow | null>(null);
  const [assignForm, setAssignForm] = useState({ ...emptyAssign });
  const [assigning, setAssigning] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([]);
  const [assignUser, setAssignUser] = useState<AssignableUser | null>(null);
  const [usersLoading, setUsersLoading] = useState(false);

  // Edit dialog
  const [editStaff, setEditStaff] = useState<OfficeStaffRow | null>(null);
  const [editForm, setEditForm] = useState<OfficeStaffUpdateRequest & { joiningDate: string }>({
    designation: '', department: '', joiningDate: today(), isPrimaryOffice: false,
    canCreateRequests: true, canUploadDocuments: true, canTrackRequests: true, notes: '',
  });
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setOffices((await OfficeApi.list({ page: 0, size: 100 })) || []); }
    catch (e: any) { setError(e?.response?.data?.message || 'Failed to load offices'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadStaff = useCallback(async (officeId: number) => {
    setStaffLoading(true); setError(null);
    try { setStaff((await OfficeStaffApi.listByOffice(officeId)) || []); }
    catch (e: any) { setError(e?.response?.data?.message || 'Failed to load staff'); setStaff([]); }
    finally { setStaffLoading(false); }
  }, []);

  const openStaff = (row: OfficeRow) => {
    setSelected(row);
    setStaff([]);
    loadStaff(row.id);
  };

  const closeStaff = () => {
    if (staffLoading || assigning || editing) return;
    setSelected(null);
    setStaff([]);
  };

  // ---- Assign ----
  const loadAssignableUsers = useCallback(async () => {
    setUsersLoading(true);
    try { setAssignableUsers((await OfficeStaffApi.listAssignableUsers()) || []); }
    catch (e: any) { setError(e?.response?.data?.message || 'Failed to load users'); setAssignableUsers([]); }
    finally { setUsersLoading(false); }
  }, []);

  const openAssign = (row: OfficeRow) => {
    setAssignOffice(row);
    setAssignForm({ ...emptyAssign });
    setAssignUser(null);
    setError(null);
    loadAssignableUsers();
  };

  const closeAssign = () => {
    if (assigning) return;
    setAssignOffice(null);
    setAssignUser(null);
  };

  const handleAssign = async () => {
    if (!assignOffice || !assignUser) return;
    const userId = assignUser.id;
    setAssigning(true); setError(null);
    try {
      await OfficeStaffApi.assign({
        userId, officeId: assignOffice.id,
        designation: assignForm.designation || undefined,
        department: assignForm.department || undefined,
        joiningDate: assignForm.joiningDate || undefined,
        isPrimaryOffice: assignForm.isPrimaryOffice,
        canCreateRequests: assignForm.canCreateRequests,
        canUploadDocuments: assignForm.canUploadDocuments,
        canTrackRequests: assignForm.canTrackRequests,
        notes: assignForm.notes || undefined,
      });
      setAssignOffice(null);
      setAssignUser(null);
      load(); // refresh staffCount
      if (selected && selected.id === assignOffice.id) loadStaff(assignOffice.id);
    } catch (e: any) { setError(e?.response?.data?.message || 'Assign failed'); }
    finally { setAssigning(false); }
  };

  // ---- Edit ----
  const openEdit = (s: OfficeStaffRow) => {
    setEditStaff(s);
    setEditForm({
      designation: s.designation || '',
      department: s.department || '',
      joiningDate: s.joiningDate || today(),
      isPrimaryOffice: !!s.isPrimaryOffice,
      canCreateRequests: s.canCreateRequests ?? true,
      canUploadDocuments: s.canUploadDocuments ?? true,
      canTrackRequests: s.canTrackRequests ?? true,
      notes: '',
    });
    setError(null);
  };

  const closeEdit = () => {
    if (editing) return;
    setEditStaff(null);
  };

  const handleEdit = async () => {
    if (!editStaff) return;
    setEditing(true); setError(null);
    try {
      await OfficeStaffApi.update(editStaff.id, {
        designation: editForm.designation || undefined,
        department: editForm.department || undefined,
        joiningDate: editForm.joiningDate || undefined,
        isPrimaryOffice: editForm.isPrimaryOffice,
        canCreateRequests: editForm.canCreateRequests,
        canUploadDocuments: editForm.canUploadDocuments,
        canTrackRequests: editForm.canTrackRequests,
        notes: editForm.notes || undefined,
      });
      setEditStaff(null);
      if (selected) loadStaff(selected.id);
    } catch (e: any) { setError(e?.response?.data?.message || 'Update failed'); }
    finally { setEditing(false); }
  };

  // ---- Delete (Remove) ----
  const handleRemove = async () => {
    if (removeId == null) return;
    try { await OfficeStaffApi.remove(removeId); setRemoveId(null); if (selected) loadStaff(selected.id); load(); }
    catch (e: any) { setError(e?.response?.data?.message || 'Remove failed'); }
  };

  const officeCols: GridColDef[] = [
    { field: 'officeName', headerName: 'Office', flex: 1.4, minWidth: 180, renderCell: (p) => p.value || `Office #${p.row.id}` },
    { field: 'officeType', headerName: 'Type', width: 140, renderCell: (p) => p.value ? <Chip size="small" label={p.value} variant="outlined" /> : '—' },
    { field: 'city', headerName: 'City', width: 140, renderCell: (p) => p.value || '—' },
    { field: 'state', headerName: 'State', width: 130, renderCell: (p) => p.value || '—' },
    { field: 'staffCount', headerName: 'Staff', width: 90, renderCell: (p) => p.value ?? '—' },
    { field: 'officerCount', headerName: 'Officers', width: 100, renderCell: (p) => p.value ?? '—' },
    {
      field: 'actions', headerName: 'Actions', width: 230, sortable: false,
      renderCell: (p) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" onClick={() => openStaff(p.row)} sx={{ textTransform: 'none' }}>View Staff</Button>
          <Button size="small" variant="contained" startIcon={<AssignIcon />} onClick={() => openAssign(p.row)} sx={{ textTransform: 'none' }}>Assign</Button>
        </Stack>
      ),
    },
  ];

  // Shared permission checkboxes (controlled by a setter + form)
  const permControls = (form: typeof assignForm, set: (f: typeof assignForm) => void) => (
    <Stack spacing={0.5}>
      <FormControlLabel control={<Checkbox checked={!!form.isPrimaryOffice} onChange={(e) => set({ ...form, isPrimaryOffice: e.target.checked })} />} label="Primary office" />
      <FormControlLabel control={<Checkbox checked={!!form.canCreateRequests} onChange={(e) => set({ ...form, canCreateRequests: e.target.checked })} />} label="Can create requests" />
      <FormControlLabel control={<Checkbox checked={!!form.canUploadDocuments} onChange={(e) => set({ ...form, canUploadDocuments: e.target.checked })} />} label="Can upload documents" />
      <FormControlLabel control={<Checkbox checked={!!form.canTrackRequests} onChange={(e) => set({ ...form, canTrackRequests: e.target.checked })} />} label="Can track requests" />
    </Stack>
  );

  return (
    <PageScaffold title="Office Management" subtitle="Offices & assigned staff" icon={<OfficeIcon />}
      loading={loading && offices.length === 0} error={error} onDismissError={() => setError(null)}>
      <Box sx={{ height: 420, bgcolor: '#fff', borderRadius: 3, border: '1px solid #E5E7EB', mb: 3 }}>
        <DataGrid rows={offices} columns={officeCols} getRowId={(r) => r.id}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} disableRowSelectionOnClick />
      </Box>

      {/* View Staff */}
      <Dialog open={!!selected} onClose={closeStaff} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>
          Staff — {selected?.officeName || `Office #${selected?.id}`}
        </DialogTitle>
        <DialogContent dividers>
          {staffLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>
          ) : staff.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No staff assigned to this office. Use the “Assign” button on the office row to add staff.</Typography>
          ) : (
            <Stack spacing={1}>
              {staff.map((s) => (
                <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px dashed #E5E7EB', py: 0.5 }}>
                  <Box>
                    <Typography variant="body2">
                      {s.fullName || s.username || s.userName || `User #${s.userId}`}
                      {s.department && <Chip size="small" label={s.department} sx={{ ml: 1 }} />}
                      {s.staffRole && <Chip size="small" label={s.staffRole} sx={{ ml: 1 }} />}
                      {s.isPrimaryOffice && <Chip size="small" color="primary" label="Primary" sx={{ ml: 1 }} />}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {s.designation || ''}
                      {s.mobile || s.contactNumber ? ` · ${s.mobile || s.contactNumber}` : ''}
                      {s.email ? ` · ${s.email}` : ''}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" startIcon={<EditIcon />} onClick={() => openEdit(s)} sx={{ textTransform: 'none' }}>Edit</Button>
                    <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => setRemoveId(s.id)} sx={{ textTransform: 'none' }}>Remove</Button>
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeStaff} disabled={staffLoading || editing} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Staff */}
      <Dialog open={!!assignOffice} onClose={closeAssign} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Assign Staff — {assignOffice?.officeName || `Office #${assignOffice?.id}`}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              options={assignableUsers}
              loading={usersLoading}
              value={assignUser}
              onChange={(_e, v) => setAssignUser(v)}
              getOptionLabel={(o) => `${o.fullName} (${o.username})`}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderOption={(props, o) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body2">{o.fullName} <Chip size="small" label={o.role} sx={{ ml: 0.5 }} /></Typography>
                    <Typography variant="caption" color="text.secondary">@{o.username}{o.mobile ? ` · ${o.mobile}` : ''}</Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Select user to assign" required
                  helperText="Active LokBhavan Staff / Office Admin users"
                  InputProps={{ ...params.InputProps, endAdornment: (<>{usersLoading ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</>) }} />
              )}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Designation" value={assignForm.designation} onChange={(e) => setAssignForm({ ...assignForm, designation: e.target.value })} fullWidth />
              <TextField label="Department" value={assignForm.department} onChange={(e) => setAssignForm({ ...assignForm, department: e.target.value })} fullWidth />
            </Box>
            <TextField type="date" label="Joining Date" InputLabelProps={{ shrink: true }} value={assignForm.joiningDate}
              onChange={(e) => setAssignForm({ ...assignForm, joiningDate: e.target.value })} fullWidth />
            {permControls(assignForm, setAssignForm)}
            <TextField label="Notes" multiline minRows={2} value={assignForm.notes}
              onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeAssign} disabled={assigning} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleAssign} disabled={assigning || !assignUser}
            sx={{ textTransform: 'none', fontWeight: 600 }}>{assigning ? 'Assigning...' : 'Assign'}</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Staff */}
      <Dialog open={!!editStaff} onClose={closeEdit} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>
          Edit Staff — {editStaff?.fullName || editStaff?.username || `User #${editStaff?.userId}`}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Office: {editStaff?.officeName || `#${selected?.id}`} · User #{editStaff?.userId} (not changeable here)
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Designation" value={editForm.designation} onChange={(e) => setEditForm({ ...editForm, designation: e.target.value })} fullWidth />
              <TextField label="Department" value={editForm.department} onChange={(e) => setEditForm({ ...editForm, department: e.target.value })} fullWidth />
            </Box>
            <TextField type="date" label="Joining Date" InputLabelProps={{ shrink: true }} value={editForm.joiningDate}
              onChange={(e) => setEditForm({ ...editForm, joiningDate: e.target.value })} fullWidth />
            {permControls(editForm as unknown as typeof assignForm, (f) => setEditForm(f as unknown as OfficeStaffUpdateRequest & { joiningDate: string }))}
            <TextField label="Notes" multiline minRows={2} value={editForm.notes}
              onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeEdit} disabled={editing} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit} disabled={editing}
            sx={{ textTransform: 'none', fontWeight: 600 }}>{editing ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog open={removeId != null} title="Remove Staff" message="Remove this staff member from the office? (Soft-deleted; can be reassigned later.)"
        confirmLabel="Remove" onConfirm={handleRemove} onClose={() => setRemoveId(null)} />
    </PageScaffold>
  );
};

export default OfficeManagementPage;